import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  listSelaProjectChangelog,
  type SelaChangelogEntry,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { useAskSela } from './ask-sela/AskSelaContext';

type WorkspaceContext = {
  project: SelaProjectDetail;
};

const PAGE_SIZE = 50;

const ACTION_LABEL: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  status_changed: 'Status changed',
  generated: 'Generated',
  deleted: 'Deleted',
  uploaded: 'Uploaded',
  completed: 'Completed',
  failed: 'Failed',
};

const ENTITY_LABEL: Record<string, string> = {
  project: 'Project',
  site: 'Site',
  document: 'Document',
  graph: 'Knowledge graph',
  gate_workflow: 'Roadmap',
  gate: 'Gate',
  deliverable: 'Deliverable',
  blocker: 'Blocker',
  permit_workflow: 'Permit workflow',
  permit_requirement: 'Permit requirement',
  permit_review_cycle: 'Review cycle',
  permit_review_comment: 'Review comment',
  resubmittal_bundle: 'Resubmittal bundle',
  person: 'Roster contact',
  role_need: 'Role need',
  follow_up: 'Follow-up',
  communication_draft: 'Draft',
};

function formatTimestamp(value?: string): string {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function labelFor(map: Record<string, string>, value?: string): string {
  const key = String(value || '').trim();
  return map[key] || key.replace(/_/g, ' ') || 'Update';
}

function sourceLabel(change: SelaChangelogEntry): string {
  if (change.actorSource === 'pipeline') return 'Pipeline';
  if (change.actorSource === 'system') return 'Sela';
  if (change.actorUserId) return 'User';
  return 'System';
}

function entityTypes(changes: SelaChangelogEntry[]): string[] {
  return Array.from(new Set(changes.map((change) => change.entityType).filter(Boolean))).sort();
}

export function SelaProjectChangelogPage() {
  const { project } = useOutletContext<WorkspaceContext>();
  const { openAskSela } = useAskSela();
  const [changes, setChanges] = useState<SelaChangelogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  const loadInitial = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const page = await listSelaProjectChangelog(project.id, { limit: PAGE_SIZE });
      setChanges(page.changes);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load changelog.');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const filteredChanges = useMemo(() => {
    if (filter === 'all') return changes;
    return changes.filter((change) => change.entityType === filter);
  }, [changes, filter]);

  const filters = useMemo(() => entityTypes(changes), [changes]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    try {
      setLoadingMore(true);
      const page = await listSelaProjectChangelog(project.id, { limit: PAGE_SIZE, cursor: nextCursor });
      setChanges((current) => [...current, ...page.changes]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load more changelog entries.');
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, project.id]);

  const askAboutChange = useCallback(
    (change: SelaChangelogEntry) => {
      openAskSela(
        {
          type: 'changelogEntry',
          label: change.summary || 'Changelog entry',
          sourceId: change.changeId,
          sourcePage: 'changelog',
          summary: [
            labelFor(ACTION_LABEL, change.action),
            labelFor(ENTITY_LABEL, change.entityType),
            change.createdAt ? formatTimestamp(change.createdAt) : '',
          ].filter(Boolean).join(' | '),
          metadata: {
            action: change.action,
            entityType: change.entityType,
            entityId: change.entityId,
            actorSource: change.actorSource,
          },
        },
        `What changed in this update: ${change.summary}`,
      );
    },
    [openAskSela],
  );

  return (
    <main className="flex-1 overflow-auto p-6 md:p-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#3251b7]">Project history</p>
          <h1 className="mt-2 text-2xl font-bold text-[#20283a]">Change Log</h1>
          <p className="mt-2 max-w-2xl text-sm text-[#657084]">
            Core project updates, uploads, roadmap changes, permit workflow changes, and roster follow-ups for {project.title}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadInitial()}
          className="inline-flex h-10 items-center justify-center rounded-lg border border-[#d5dce8] bg-white px-4 text-sm font-semibold text-[#2f3a4d] hover:bg-slate-50"
        >
          Refresh
        </button>
      </div>

      <section className="rounded-2xl border border-[#e1e8f0] bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-[#edf1f6] px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#20283a]">Activity feed</h2>
            <p className="text-sm text-[#657084]">{changes.length} entries loaded</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#4f5b6f]">
            Type
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="h-9 rounded-lg border border-[#d5dce8] bg-white px-3 text-sm text-[#20283a] focus:border-[#3251b7] focus:outline-none focus:ring-2 focus:ring-[#3251b7]/20"
            >
              <option value="all">All</option>
              {filters.map((type) => (
                <option key={type} value={type}>
                  {labelFor(ENTITY_LABEL, type)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-[#657084]">Loading changelog...</div>
        ) : loadError ? (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        ) : filteredChanges.length === 0 ? (
          <div className="p-8 text-sm text-[#657084]">
            {changes.length === 0 ? 'No changelog entries yet.' : 'No entries match this filter.'}
          </div>
        ) : (
          <div className="divide-y divide-[#edf1f6]">
            {filteredChanges.map((change) => (
              <article key={change.changeId} className="group px-5 py-4 transition-colors hover:bg-slate-50/70">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#eff2fe] px-2.5 py-1 text-xs font-semibold text-[#3251b7]">
                        {labelFor(ACTION_LABEL, change.action)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                        {labelFor(ENTITY_LABEL, change.entityType)}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-[#20283a]">{change.summary || 'Project updated'}</h3>
                    <p className="mt-1 text-xs text-[#7a8496]">
                      {formatTimestamp(change.createdAt)} · {sourceLabel(change)}
                      {change.entityId ? ` · ${change.entityId}` : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => askAboutChange(change)}
                    className="self-start rounded-lg border border-[#d5dce8] bg-white px-3 py-2 text-xs font-semibold text-[#3251b7] opacity-100 hover:border-[#3251b7]/40 hover:bg-[#eff2fe] md:opacity-0 md:group-hover:opacity-100"
                  >
                    Ask Sela
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && !loadError && nextCursor ? (
          <div className="border-t border-[#edf1f6] px-5 py-4">
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="inline-flex h-10 items-center justify-center rounded-lg border border-[#d5dce8] bg-white px-4 text-sm font-semibold text-[#2f3a4d] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
